/**
 * Board dimensions. Mutable so the match-4 experiment can test 7×9 (default),
 * 7×10 (vertical room), and 8×10 (larger battlefield) with the same seeds.
 * All downstream code reads these at call time, not cached.
 */
export const DEFAULT_WELL_COLS = 7;
export const DEFAULT_WELL_ROWS = 10;

export let WELL_COLS = DEFAULT_WELL_COLS;
export let WELL_ROWS = DEFAULT_WELL_ROWS;
export let WELL_SIZE = WELL_COLS * WELL_ROWS;

/** Board sizes to A/B test. Keybinding and URL param cycle through these. */
export const BOARD_SIZES: readonly { cols: number; rows: number; label: string }[] = [
  { cols: 7, rows: 9, label: '7×9' },
  { cols: 7, rows: 10, label: '7×10' },
  { cols: 8, rows: 10, label: '8×10' },
];

/**
 * Set the well dimensions. Callers must ensure no live grid spans the change —
 * typically this is set at combat start or between fights, never mid-resolve.
 */
export function setBoardSize(cols: number, rows: number): void {
  WELL_COLS = cols;
  WELL_ROWS = rows;
  WELL_SIZE = cols * rows;
}

export function resetBoardSize(): void {
  setBoardSize(DEFAULT_WELL_COLS, DEFAULT_WELL_ROWS);
}

/** Current size as a display label, e.g. "7×9". */
export function boardLabel(): string {
  return `${WELL_COLS}×${WELL_ROWS}`;
}

/** True when the board is not at its default size. */
export function isDefaultBoard(): boolean {
  return WELL_COLS === DEFAULT_WELL_COLS && WELL_ROWS === DEFAULT_WELL_ROWS;
}

export type GemKind = 'ember' | 'frost' | 'venom' | 'volt' | 'stone';

export const GEM_KINDS: readonly GemKind[] = [
  'ember', 'frost', 'venom', 'volt', 'stone',
] as const;

/** Stone is inert: never matches, never yields an effect. */
export function isMatchable(kind: GemKind): boolean {
  return kind !== 'stone';
}

export type Cell = GemKind | null;

/**
 * Flat, row-major. Index = row * WELL_COLS + col. Row 0 is the TOP of the well,
 * gravity pulls toward row 8. A flat array (rather than Cell[][]) keeps
 * noUncheckedIndexedAccess usable in the resolve hot loop: one `?? null` per
 * access instead of two, and a cell key IS the array index.
 */
export type Grid = Cell[];

export type CellIndex = number;

export function idx(row: number, col: number): CellIndex {
  return row * WELL_COLS + col;
}

export function idxRow(i: CellIndex): number {
  return Math.floor(i / WELL_COLS);
}

export function idxCol(i: CellIndex): number {
  return i % WELL_COLS;
}

export function inBounds(row: number, col: number): boolean {
  return row >= 0 && row < WELL_ROWS && col >= 0 && col < WELL_COLS;
}

/** Piece cells as (dr, dc) offsets from an anchor. dr grows downward. */
export type CellOffset = readonly [dr: number, dc: number];
export type PieceCells = readonly CellOffset[];

/** A piece ready to drop: normalized cells plus one gem kind per cell. */
export interface Piece {
  readonly cells: PieceCells;
  readonly kinds: readonly GemKind[];
}
